/**
 * The shell: a navigation rail, a route, and one keyboard shortcut.
 *
 * Three places — a dashboard of what needs a person, a queue of legajos, and one legajo — each at
 * its own URL, so an analyst can send a colleague a link. Ctrl/⌘+K jumps to any legajo by name or
 * CUIT without touching the mouse, which is how a queue of hundreds is actually navigated.
 */

import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getToken, setSignedOut, setToken, takeReturnTo, wasSignedOut } from "./api/client";
import { ROLES, label as labelOf } from "./lib/labels";
import { canEditPolicy } from "./lib/model";
import { Button, Spinner } from "./components/ui";
import CommandPalette from "./components/CommandPalette";
import { BancorLogo } from "./components/BancorLogo";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Portfolio from "./pages/Portfolio";
import CaseView from "./pages/CaseView";
import NewCaseDialog from "./pages/NewCaseDialog";
import Policy from "./pages/Policy";

export default function App() {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(getToken());
  const [signedOut, setSignedOutState] = useState(wasSignedOut());
  const [expired, setExpired] = useState(false);

  // Una sesión que vence en medio de una revisión tiene que llevar a la pantalla de ingreso, no
  // dejar al analista mirando un error de acceso donde estaba. Lo que estuviera escribiendo ya está
  // a salvo en su borrador; esto se encarga de traerlo de vuelta al mismo lugar después.
  useEffect(() => {
    function onExpired() {
      setExpired(true);
      setTokenState(null);
      queryClient.clear();
    }
    window.addEventListener("bancor:session-expired", onExpired);
    return () => window.removeEventListener("bancor:session-expired", onExpired);
  }, [queryClient]);

  // Ask the API who we are before asking for a password: the session may already be valid — on a
  // reload with the key still in this tab, or behind a proxy that signs requests in development.
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.whoami,
    retry: false,
    staleTime: Infinity,
  });

  if (session.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Verificando la sesión…" />
      </div>
    );
  }

  if (expired || signedOut || (!token && !session.data)) {
    return (
      <Login
        expired={expired}
        onSignedIn={() => {
          setSignedOut(false);
          setSignedOutState(false);
          setExpired(false);
          setTokenState(getToken());
          void queryClient.invalidateQueries();
          const back = takeReturnTo();
          if (back && back !== window.location.pathname) window.history.replaceState({}, "", back);
        }}
      />
    );
  }

  return (
    <Shell
      onSignOut={async () => {
        try {
          await api.logout();
        } catch {
          /* the session may already be gone; the local state is what matters here */
        }
        setToken(null);
        setTokenState(null);
        setSignedOut(true);
        setSignedOutState(true);
        queryClient.clear();
      }}
    />
  );
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [palette, setPalette] = useState(false);

  const me = useQuery({ queryKey: ["session"], queryFn: api.whoami, staleTime: Infinity });
  const canSeePolicy = canEditPolicy(me.data?.role);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-full">
      <aside className="flex w-60 shrink-0 flex-col bg-rail text-rail-text">
        <div className="border-b border-rail-line px-5 pt-6 pb-4">
          {/* The bank's mark first, then what this particular tool is: an analyst opens several
              internal systems in a morning and should know which one this is at a glance. */}
          <BancorLogo className="h-5 w-auto text-white" />
          <div className="mt-2.5 text-sm font-semibold text-white">Análisis de Crédito</div>
          <div className="text-[0.7rem] tracking-widest text-white/50 uppercase">
            Riesgo Corporativo
          </div>
        </div>

        <div className="px-4 py-4">
          <Button variant="primary" className="w-full" onClick={() => setCreating(true)}>
            + Nuevo legajo
          </Button>
        </div>

        {/* The sections of the platform. The legajos themselves live in the cartera and are
            reached from there or with ⌘K — a rail that also listed them made the list compete
            with the sections, and neither read as the place to go. */}
        <nav className="flex-1 space-y-1 px-4">
          <RailLink to="/">Inicio</RailLink>
          <RailLink to="/legajos">Cartera de legajos</RailLink>
          {/* La política de crédito es del banco, pero no la edita cualquiera: el riel la muestra
              a quien puede tocarla. La API la exige igual, así que esconderla es cortesía, no
              seguridad. */}
          {canSeePolicy ?
            <RailLink to="/politica">Política de crédito</RailLink>
          : null}
          <button
            onClick={() => setPalette(true)}
            className="flex w-full items-center justify-between rounded-lg border border-l-[3px] border-transparent px-4 py-2 text-sm hover:bg-white/5"
          >
            Buscar legajo
            <kbd className="rounded border border-white/20 px-1 text-[0.65rem] text-white/45">
              ⌘K
            </kbd>
          </button>
        </nav>

        <div className="border-t border-rail-line px-5 py-3 text-xs text-white/45">
          <div className="truncate text-white/80">{me.data?.name ?? "…"}</div>
          <div className="truncate">{labelOf(ROLES, me.data?.role, "")}</div>
          <button className="mt-1 underline hover:text-white" onClick={onSignOut}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1500px] px-8 py-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/legajos" element={<Portfolio />} />
            <Route path="/legajos/:caseId" element={<CaseRoute />} />
            <Route path="/politica" element={<Policy />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      {creating ?
        <NewCaseDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(`/legajos/${id}`);
          }}
        />
      : null}

      {palette ?
        <CommandPalette
          onClose={() => setPalette(false)}
          onPick={(id) => {
            setPalette(false);
            navigate(`/legajos/${id}`);
          }}
        />
      : null}
    </div>
  );
}

function RailLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `block rounded-lg border border-l-[3px] px-4 py-2 text-sm ${
          isActive ?
            "border-white/15 border-l-accent bg-white/10 text-white"
          : "border-transparent hover:bg-white/5"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function CaseRoute() {
  const { caseId } = useParams();
  // A stale link to an archived or purged legajo should still land on something readable.
  const cases = useQuery({
    queryKey: ["cases", "route"],
    queryFn: () => api.listCases({ size: 1 }),
  });
  if (!caseId) return <Navigate to="/legajos" replace />;
  if (cases.isLoading) return <Spinner />;
  return <CaseView caseId={caseId} />;
}
